import React, { useState, useEffect, useMemo } from "react";
import {
  Card,
  Table,
  Typography,
  Tag,
  Space,
  Button,
  Row,
  Col,
  Statistic,
  DatePicker,
  Select,
  Modal,
  Form,
  Input,
  InputNumber,
  theme,
  Alert,
  Tooltip,
  Popconfirm,
  message,
} from "antd";
import {
  PlusOutlined,
  DollarOutlined,
  BankOutlined,
  UserOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  FileTextOutlined,
  DeleteOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import PageLayout from "@components/pageLayout/PageLayout.react";
import { makePagination } from "@components/common/TablePagination";
import dayjs from "dayjs";
import { authenticatedFetch } from "@/libs/api";

const { Title, Text, Paragraph } = Typography;
const { RangePicker } = DatePicker;
const { TextArea } = Input;

// API helper
const fetchWithAuth = async (url, options = {}) => {
  const response = await authenticatedFetch(url, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.message || err.error || `HTTP ${response.status}`);
  }
  return response.json();
};

// Status configuration
const statusConfig = {
  pending: { color: "processing", icon: <ClockCircleOutlined />, text: "Pending" },
  applied: { color: "success", icon: <CheckCircleOutlined />, text: "Applied" },
  cancelled: { color: "default", icon: <CloseCircleOutlined />, text: "Cancelled" },
};

// Journal type configuration
const journalTypeConfig = {
  onboarding_bonus: { color: "green", text: "Onboarding Bonus", icon: <PlusOutlined /> },
  offboarding_fee: { color: "red", text: "Offboarding Fee", icon: <CloseCircleOutlined /> },
  manual_credit: { color: "blue", text: "Manual Credit", icon: <DollarOutlined /> },
  manual_debit: { color: "orange", text: "Manual Debit", icon: <DollarOutlined /> },
  refund: { color: "cyan", text: "Refund", icon: <DollarOutlined /> },
  penalty: { color: "red", text: "Penalty", icon: <CloseCircleOutlined /> },
};

// Entity type configuration
const entityTypeConfig = {
  company: { color: "purple", text: "Company", icon: <BankOutlined /> },
  customer: { color: "blue", text: "Customer", icon: <UserOutlined /> },
  system: { color: "green", text: "System", icon: <FileTextOutlined /> },
};

// Summary stat card component
function SummaryStatCard({ title, value, prefix, icon, color, loading }) {
  const { token } = theme.useToken();

  return (
    <Card bordered={false} style={{ borderRadius: 12, height: "100%" }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
        <div>
          <Text type="secondary" style={{ fontSize: 13, display: "block", marginBottom: 4 }}>{title}</Text>
          <Statistic
            value={loading ? "—" : value}
            prefix={prefix}
            valueStyle={{ fontSize: 24, fontWeight: 700, color: token.colorText }}
            loading={loading}
          />
        </div>
        <div
          style={{
            width: 44,
            height: 44,
            borderRadius: 10,
            background: color || "rgba(84, 162, 204, 0.1)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 20,
            color: token.colorPrimary,
          }}
        >
          {icon}
        </div>
      </div>
    </Card>
  );
}

export default function SpecialJournals() {
  const { token: themeToken } = theme.useToken();
  const [pageSize, setPageSize] = useState(20);
  const [loading, setLoading] = useState(true);
  const [journals, setJournals] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, limit: 100, offset: 0 });
  const [filters, setFilters] = useState({
    dateRange: [dayjs().subtract(30, "day"), dayjs().add(7, "day")],
    status: null,
    journalType: null,
    entityType: null,
  });

  // Modal state
  const [modalVisible, setModalVisible] = useState(false);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedJournal, setSelectedJournal] = useState(null);
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  // Fetch journals
  const fetchJournals = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.dateRange && filters.dateRange[0] && filters.dateRange[1]) {
        params.set("startDate", filters.dateRange[0].format("YYYY-MM-DD"));
        params.set("endDate", filters.dateRange[1].format("YYYY-MM-DD"));
      }
      if (filters.status) params.set("status", filters.status);
      if (filters.journalType) params.set("journalType", filters.journalType);
      if (filters.entityType) params.set("entityType", filters.entityType);
      params.set("limit", "100");

      const result = await fetchWithAuth(`/api/settlements/journals?${params.toString()}`);
      setJournals(result.data?.journals || []);
      setPagination(result.data?.pagination || { total: 0, limit: 100, offset: 0 });
    } catch (error) {
      console.error("Failed to fetch journals:", error);
      message.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Fetch companies and customers for dropdowns
  const fetchEntities = async () => {
    try {
      const [companiesRes, customersRes] = await Promise.all([
        fetchWithAuth("/api/companies"),
        fetchWithAuth("/api/customers?limit=500"),
      ]);
      setCompanies(companiesRes.data || companiesRes || []);
      setCustomers(customersRes.data || []);
    } catch (error) {
      console.error("Failed to fetch entities:", error);
    }
  };

  useEffect(() => {
    fetchJournals();
    fetchEntities();
  }, []);

  useEffect(() => {
    fetchJournals();
  }, [filters.dateRange, filters.status, filters.journalType, filters.entityType]);

  // Calculate summary stats
  const summaryStats = useMemo(() => {
    const pending = journals.filter(j => j.status === "pending");
    const pendingCredits = pending.filter(j => j.amount > 0).reduce((sum, j) => sum + Number(j.amount), 0);
    const pendingDebits = pending.filter(j => j.amount < 0).reduce((sum, j) => sum + Math.abs(Number(j.amount)), 0);
    return {
      pendingCount: pending.length,
      pendingCredits,
      pendingDebits,
      total: journals.length,
    };
  }, [journals]);

  // Handle create journal
  const handleCreate = async (values) => {
    setSubmitting(true);
    try {
      // Adjust amount sign based on journal type
      let amount = Math.abs(values.amount);
      if (["manual_debit", "offboarding_fee", "penalty"].includes(values.journalType)) {
        amount = -amount;
      }

      await fetchWithAuth("/api/settlements/journals", {
        method: "POST",
        body: JSON.stringify({
          ...values,
          amount,
          effectiveDate: values.effectiveDate.format("YYYY-MM-DD"),
        }),
      });
      message.success("Journal created successfully");
      setModalVisible(false);
      form.resetFields();
      fetchJournals();
    } catch (error) {
      message.error(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Handle cancel journal
  const handleCancel = async (journal) => {
    try {
      await fetchWithAuth(`/api/settlements/journals/${journal.journal_id}/cancel`, {
        method: "PUT",
        body: JSON.stringify({ reason: "Cancelled by user" }),
      });
      message.success("Journal cancelled");
      fetchJournals();
    } catch (error) {
      message.error(error.message);
    }
  };

  // View journal details
  const handleViewDetails = (journal) => {
    setSelectedJournal(journal);
    setDetailModalVisible(true);
  };

  // Table columns
  const columns = [
    {
      title: "Journal ID",
      dataIndex: "journal_id",
      key: "journal_id",
      width: 180,
      render: (id, record) => (
        <Text
          code
          style={{ fontSize: 12, cursor: "pointer", color: themeToken.colorPrimary }}
          onClick={() => handleViewDetails(record)}
        >
          {id}
        </Text>
      ),
    },
    {
      title: "Type",
      dataIndex: "journal_type",
      key: "journal_type",
      width: 150,
      render: (type) => {
        const config = journalTypeConfig[type] || { color: "default", text: type };
        return <Tag color={config.color}>{config.text}</Tag>;
      },
    },
    {
      title: "Entity",
      key: "entity",
      width: 200,
      render: (_, record) => {
        const config = entityTypeConfig[record.entity_type] || { color: "default", text: record.entity_type };
        return (
          <Space direction="vertical" size={0}>
            <Tag color={config.color} icon={config.icon}>{config.text}</Tag>
            <Text style={{ fontSize: 12 }}>{record.entity_name || record.entity_id}</Text>
          </Space>
        );
      },
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      width: 250,
      ellipsis: true,
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      width: 120,
      align: "right",
      render: (amount) => {
        const num = Number(amount);
        const isCredit = num > 0;
        return (
          <Text style={{ color: isCredit ? themeToken.colorSuccess : themeToken.colorError, fontWeight: 600 }}>
            {isCredit ? "+" : ""}${num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </Text>
        );
      },
    },
    {
      title: "Effective Date",
      dataIndex: "effective_date",
      key: "effective_date",
      width: 120,
      render: (date) => dayjs(date).format("YYYY-MM-DD"),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 110,
      render: (status) => {
        const config = statusConfig[status] || { color: "default", icon: null, text: status };
        return (
          <Tag color={config.color} icon={config.icon}>
            {config.text}
          </Tag>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: 100,
      fixed: "right",
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="View Details">
            <Button
              type="text"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => handleViewDetails(record)}
            />
          </Tooltip>
          {record.status === "pending" && (
            <Popconfirm
              title="Cancel this journal?"
              description="This action cannot be undone."
              onConfirm={() => handleCancel(record)}
              okText="Yes"
              cancelText="No"
            >
              <Tooltip title="Cancel">
                <Button type="text" size="small" danger icon={<DeleteOutlined />} />
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  // Watch entity type changes in form
  const watchedEntityType = Form.useWatch("entityType", form);

  return (
    <PageLayout active="specialJournals">
      {/* Page Header */}
      <div style={{ marginBottom: 24, display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <Title level={2} style={{ marginBottom: 4 }}>Special Journals</Title>
          <Text type="secondary" style={{ fontSize: 14 }}>
            Manual credits, debits, onboarding bonuses, and other adjustments for companies and customers.
          </Text>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setModalVisible(true)}
        >
          Create Journal
        </Button>
      </div>

      {/* Info Alert */}
      <Alert
        message="Special Journals are included in settlement"
        description="Pending journals for a settlement date will be automatically applied during the daily settlement run. Applied journals cannot be cancelled."
        type="info"
        showIcon
        style={{ marginBottom: 24 }}
      />

      {/* Summary Stats */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <SummaryStatCard
            title="Pending Journals"
            value={summaryStats.pendingCount}
            icon={<ClockCircleOutlined />}
            color="rgba(250, 173, 20, 0.1)"
            loading={loading}
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <SummaryStatCard
            title="Pending Credits"
            value={summaryStats.pendingCredits.toLocaleString(undefined, { minimumFractionDigits: 2 })}
            prefix="$"
            icon={<DollarOutlined />}
            color="rgba(82, 196, 26, 0.1)"
            loading={loading}
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <SummaryStatCard
            title="Pending Debits"
            value={summaryStats.pendingDebits.toLocaleString(undefined, { minimumFractionDigits: 2 })}
            prefix="$"
            icon={<DollarOutlined />}
            color="rgba(255, 77, 79, 0.1)"
            loading={loading}
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <SummaryStatCard
            title="Total Journals"
            value={pagination.total}
            icon={<FileTextOutlined />}
            color="rgba(84, 162, 204, 0.1)"
            loading={loading}
          />
        </Col>
      </Row>

      {/* Filters */}
      <Card bordered={false} style={{ borderRadius: 12, marginBottom: 16 }}>
        <Space wrap size={16}>
          <Space>
            <Text type="secondary">Date Range:</Text>
            <RangePicker
              value={filters.dateRange}
              onChange={(dates) => setFilters({ ...filters, dateRange: dates })}
              allowClear
            />
          </Space>
          <Space>
            <Text type="secondary">Status:</Text>
            <Select
              style={{ width: 140 }}
              placeholder="All Status"
              allowClear
              value={filters.status}
              onChange={(value) => setFilters({ ...filters, status: value })}
              options={Object.entries(statusConfig).map(([key, config]) => ({
                value: key,
                label: config.text,
              }))}
            />
          </Space>
          <Space>
            <Text type="secondary">Type:</Text>
            <Select
              style={{ width: 160 }}
              placeholder="All Types"
              allowClear
              value={filters.journalType}
              onChange={(value) => setFilters({ ...filters, journalType: value })}
              options={Object.entries(journalTypeConfig).map(([key, config]) => ({
                value: key,
                label: config.text,
              }))}
            />
          </Space>
          <Space>
            <Text type="secondary">Entity:</Text>
            <Select
              style={{ width: 120 }}
              placeholder="All"
              allowClear
              value={filters.entityType}
              onChange={(value) => setFilters({ ...filters, entityType: value })}
              options={Object.entries(entityTypeConfig).map(([key, config]) => ({
                value: key,
                label: config.text,
              }))}
            />
          </Space>
        </Space>
      </Card>

      {/* Journals Table */}
      <Card
        bordered={false}
        style={{ borderRadius: 12 }}
        title={
          <Space>
            <FileTextOutlined style={{ color: themeToken.colorPrimary }} />
            <span style={{ fontSize: 16, fontWeight: 600 }}>Journal Entries</span>
          </Space>
        }
        extra={
          <Text type="secondary" style={{ fontSize: 13 }}>
            {journals.length} journals
          </Text>
        }
        styles={{
          header: {
            borderBottom: `1px solid ${themeToken.colorBorderSecondary}`,
            padding: "12px 16px",
            minHeight: 48,
          },
          body: { padding: 0 },
        }}
      >
        <Table
          columns={columns}
          dataSource={journals}
          rowKey="journal_id"
          loading={loading}
          pagination={makePagination(pageSize, setPageSize)}
          scroll={{ x: 1200 }}
        />
      </Card>

      {/* Create Journal Modal */}
      <Modal
        title="Create Special Journal"
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleCreate}
          initialValues={{
            effectiveDate: dayjs(),
          }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="entityType"
                label="Entity Type"
                rules={[{ required: true, message: "Required" }]}
              >
                <Select
                  placeholder="Select entity type"
                  options={[
                    { value: "company", label: "Company" },
                    { value: "customer", label: "Customer" },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="entityId"
                label="Entity"
                rules={[{ required: true, message: "Required" }]}
              >
                <Select
                  showSearch
                  placeholder={watchedEntityType ? `Select ${watchedEntityType}` : "Select entity type first"}
                  disabled={!watchedEntityType}
                  filterOption={(input, option) =>
                    (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                  }
                  options={
                    watchedEntityType === "company"
                      ? companies.map(c => ({ value: c.id, label: c.company_name || c.id }))
                      : watchedEntityType === "customer"
                        ? customers.map(c => ({ value: c.id, label: c.name || c.email || c.id }))
                        : []
                  }
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="journalType"
                label="Journal Type"
                rules={[{ required: true, message: "Required" }]}
              >
                <Select
                  placeholder="Select type"
                  options={Object.entries(journalTypeConfig).map(([key, config]) => ({
                    value: key,
                    label: config.text,
                  }))}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="effectiveDate"
                label="Effective Date"
                rules={[{ required: true, message: "Required" }]}
              >
                <DatePicker style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="description"
            label="Description"
            rules={[{ required: true, message: "Required" }]}
          >
            <Input placeholder="e.g., Onboarding bonus for system AL2002017100026" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="amount"
                label="Amount ($)"
                rules={[{ required: true, message: "Required" }]}
              >
                <InputNumber
                  style={{ width: "100%" }}
                  min={0}
                  precision={2}
                  placeholder="0.00"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="unitPrice" label="Unit Price ($)">
                <InputNumber style={{ width: "100%" }} min={0} precision={2} placeholder="Optional" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="quantity" label="Quantity">
                <InputNumber style={{ width: "100%" }} min={1} placeholder="Optional" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="systemSn" label="System SN (optional)">
            <Input placeholder="e.g., AL2002017100026" />
          </Form.Item>

          <Form.Item name="notes" label="Notes (optional)">
            <TextArea rows={3} placeholder="Additional notes..." />
          </Form.Item>

          <div style={{ textAlign: "right", marginTop: 24 }}>
            <Space>
              <Button onClick={() => setModalVisible(false)}>Cancel</Button>
              <Button type="primary" htmlType="submit" loading={submitting}>
                Create Journal
              </Button>
            </Space>
          </div>
        </Form>
      </Modal>

      {/* Journal Detail Modal */}
      <Modal
        title="Journal Details"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={
          <Button onClick={() => setDetailModalVisible(false)}>Close</Button>
        }
        width={600}
      >
        {selectedJournal && (
          <div>
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Text type="secondary">Journal ID</Text>
                <Paragraph copyable style={{ marginBottom: 0 }}>{selectedJournal.journal_id}</Paragraph>
              </Col>
              <Col span={12}>
                <Text type="secondary">Status</Text>
                <div>
                  <Tag color={statusConfig[selectedJournal.status]?.color}>
                    {statusConfig[selectedJournal.status]?.text || selectedJournal.status}
                  </Tag>
                </div>
              </Col>
              <Col span={12}>
                <Text type="secondary">Entity Type</Text>
                <div>
                  <Tag color={entityTypeConfig[selectedJournal.entity_type]?.color}>
                    {entityTypeConfig[selectedJournal.entity_type]?.text || selectedJournal.entity_type}
                  </Tag>
                </div>
              </Col>
              <Col span={12}>
                <Text type="secondary">Entity</Text>
                <Paragraph style={{ marginBottom: 0 }}>{selectedJournal.entity_name || selectedJournal.entity_id}</Paragraph>
              </Col>
              <Col span={12}>
                <Text type="secondary">Journal Type</Text>
                <div>
                  <Tag color={journalTypeConfig[selectedJournal.journal_type]?.color}>
                    {journalTypeConfig[selectedJournal.journal_type]?.text || selectedJournal.journal_type}
                  </Tag>
                </div>
              </Col>
              <Col span={12}>
                <Text type="secondary">Amount</Text>
                <Paragraph style={{ marginBottom: 0, fontWeight: 600, color: Number(selectedJournal.amount) > 0 ? themeToken.colorSuccess : themeToken.colorError }}>
                  {Number(selectedJournal.amount) > 0 ? "+" : ""}${Number(selectedJournal.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </Paragraph>
              </Col>
              <Col span={12}>
                <Text type="secondary">Effective Date</Text>
                <Paragraph style={{ marginBottom: 0 }}>{dayjs(selectedJournal.effective_date).format("YYYY-MM-DD")}</Paragraph>
              </Col>
              <Col span={12}>
                <Text type="secondary">Created At</Text>
                <Paragraph style={{ marginBottom: 0 }}>{dayjs(selectedJournal.created_at).format("YYYY-MM-DD HH:mm")}</Paragraph>
              </Col>
              <Col span={24}>
                <Text type="secondary">Description</Text>
                <Paragraph style={{ marginBottom: 0 }}>{selectedJournal.description}</Paragraph>
              </Col>
              {selectedJournal.system_sn && (
                <Col span={12}>
                  <Text type="secondary">System SN</Text>
                  <Paragraph style={{ marginBottom: 0 }}>{selectedJournal.system_sn}</Paragraph>
                </Col>
              )}
              {selectedJournal.unit_price && (
                <Col span={12}>
                  <Text type="secondary">Unit Price</Text>
                  <Paragraph style={{ marginBottom: 0 }}>${Number(selectedJournal.unit_price).toFixed(2)}</Paragraph>
                </Col>
              )}
              {selectedJournal.quantity && (
                <Col span={12}>
                  <Text type="secondary">Quantity</Text>
                  <Paragraph style={{ marginBottom: 0 }}>{selectedJournal.quantity}</Paragraph>
                </Col>
              )}
              {selectedJournal.applied_run_id && (
                <Col span={24}>
                  <Text type="secondary">Applied in Run</Text>
                  <Paragraph copyable style={{ marginBottom: 0 }}>{selectedJournal.applied_run_id}</Paragraph>
                </Col>
              )}
              {selectedJournal.notes && (
                <Col span={24}>
                  <Text type="secondary">Notes</Text>
                  <Paragraph style={{ marginBottom: 0 }}>{selectedJournal.notes}</Paragraph>
                </Col>
              )}
              {selectedJournal.cancel_reason && (
                <Col span={24}>
                  <Text type="secondary">Cancel Reason</Text>
                  <Paragraph style={{ marginBottom: 0, color: themeToken.colorError }}>{selectedJournal.cancel_reason}</Paragraph>
                </Col>
              )}
            </Row>
          </div>
        )}
      </Modal>
    </PageLayout>
  );
}
